import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { useCompany } from "@/contexts/CompanyContext";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const { company } = useCompany();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success(`Welcome, ${res.user.name}`);
    const rs = Array.isArray(res.user.roles) ? res.user.roles : [res.user.role];
    if (rs.some((r) => ["admin", "operations", "kitchen", "setup"].includes(r))) {
      navigate("/dashboard");
    } else if (rs.includes("driver")) {
      navigate("/driver");
    } else {
      navigate("/portal");
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Left: artwork */}
      <div className="hidden lg:flex w-1/2 relative noise">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/83fd6fba-eb65-4054-a0b8-15caa42f020e/images/12a9a4a7b09a15f78c002043c53b6849be2961f6e5c14da44e47ee875ee86917.png"
          alt="luxury"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandMark size={40} eyebrow="Established Excellence" />
          </div>
          <div className="space-y-3">
            <div className="label-eyebrow">{t("brand.tagline")}</div>
            <h1 className="font-display text-5xl xl:text-6xl leading-[1.05] tracking-tight">
              Where every<br /><span className="gold-text italic">soirée</span> begins.
            </h1>
            <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
              The command centre for weddings, corporate galas, dessert tables and live cooking stations across Kuala Lumpur.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#050505] relative">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-8">
            <BrandMark size={40} eyebrow="Premium Service" />
          </div>

          <div className="label-eyebrow">Sign In</div>
          <h2 className="font-display text-4xl mt-2 mb-2">{t("login.title")}</h2>
          <p className="text-zinc-500 text-sm mb-8">{t("login.subtitle")}</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label className="label-eyebrow" htmlFor="email">{t("login.email")}</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="bg-[#0a0a0a] border-[#2a2a2a] text-white h-11 rounded-sm focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow" htmlFor="password">{t("login.password")}</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="bg-[#0a0a0a] border-[#2a2a2a] text-white h-11 rounded-sm focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
              />
            </div>
            {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-sm bg-[#D4AF37] text-black hover:bg-[#E5C86B] font-medium tracking-wide"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>{t("login.submit")} <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>

          <div className="mt-8 text-xs text-zinc-600 text-center space-y-2">
            <div>Don't have an account? <Link to="/signup" className="text-[#D4AF37] hover:text-[#E5C86B]" data-testid="signup-link">Request access →</Link></div>
            <div>Have a tracking code? <Link to="/track" className="text-[#D4AF37] hover:text-[#E5C86B]">Track your event →</Link></div>
          </div>
        </div>
      </div>
    </div>
  );
}
